import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IActiveFilter, ISyncAgentsDisplay } from '@app/models';
import { authService } from '@app/services';
import { currentUser } from '@app/store/actions';
import { store } from '@app/helpers/store';
import { displayName } from '@app/helpers';

class VaultsService {
    readonly apiBase: string = '/api/directory/Vaults';

    getVaults(orderBy: string, asc: string, top: number, skip: number, organizationId: string, handleError: Function, handleSuccess: Function, isReset: boolean, filters?: IActiveFilter[]): AxiosPromise {
        return apiService
            .get(this.apiBase, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? {$filter: manageFilters(filters, organizationId)} : {$filter: manageFilters([], organizationId)})
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getVault(vaultId: string, handleError: Function, handleSuccess: Function) {
        return apiService
            .get(`${this.apiBase}(${vaultId})?$expand=PasswordServerPolicy,VaultGroups,Owner`, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    hasIndividualVault(handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl = `${this.apiBase}/HasIndividualVault`;

        return apiService
            .get(apiUrl, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    doVaultComplexSearch(searchTerm: string, searchType: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl = `${this.apiBase}/DoVaultComplexSearch?searchTerm=${searchTerm}&searchType=${searchType}`;

        return apiService
            .get(apiUrl, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    isVaultNameAvailable(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl = `${this.apiBase}/IsVaultNameAvailable`;

        return apiService
            .post(apiUrl, jsonObject, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    addVault(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl = `${this.apiBase}`;

        return apiService
            .post(apiUrl, jsonObject, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    addIndividualVault(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl = `${this.apiBase}/AddIndividualVault`;

        return apiService
            .post(apiUrl, jsonObject, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    patchVault(jsonObject: any, vaultId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .patch(`${this.apiBase}(${vaultId})`, jsonObject, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getModifiableVaults(orderBy: string, asc: string, top: number, skip: number, organizationId: string, handleError: Function,
                        handleSuccess: Function, isReset: boolean, filterText: boolean, search?: string, vaultType = '', onBehalfOf = '', filters?: IActiveFilter[]): AxiosPromise {
        let filter: string = '';
        if (filterText && search && search !== '') {
            filter = `(contains(DisplayName,'${search}') or contains(cast(Id, 'Edm.String'), '${search}')) AND OrganizationId eq ${organizationId}`;
        }

        return apiService
            .get(this.apiBase + '/ModifiableVaults', {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    $filter: filter || manageFilters(filters, organizationId)
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    async getAllModifiableVaults(asc: string, organizationId: string, skip: number = 0, values: any[] = []): Promise<any[]> {
        const resp: AxiosResponse = await this.getModifiableVaults(displayName, asc, 100, skip, organizationId, () => {}, () => {}, true, false);

        skip += resp.data.value.length;
        values = [...values, ...resp.data.value];

        if (skip < resp.data['@odata.count']) {
            return this.getAllModifiableVaults(asc, organizationId, skip, values);
        } else {
            return values;
        }
    }

    getVaultsPermissions() {
        return apiService.get('api/profile/userinfo');
    }

    updateVaultsPermissions(additionalAction?: Function) {
        let userData = store.getState().signIn.currentUser;

        this.getVaultsPermissions().then((response: any) => {
            userData.vaults = response.data.vaults;
            authService.setVaultPermissions(response.data.vaults, userData);
            store.dispatch(currentUser(userData));
            authService.setUserData(userData);

            if (additionalAction !== undefined) {
                additionalAction();
            }
        }).catch();
    }

    deleteVaults(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl = `${this.apiBase}/DeleteVaults`;

        return apiService
            .post(apiUrl, jsonObject, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getVaultSyncAgents(vaultId: string, skip: number, isReset: boolean, handleError?: Function, handleSuccess?: Function, filters?: IActiveFilter[]): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${vaultId})/GetSyncAgents`, {
                params: {
                    $orderBy: 'AgentName asc',
                    $top: 100,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getAllVaultSyncAgents(vaultId: string): Promise<ISyncAgentsDisplay[]> {
        let values: ISyncAgentsDisplay[] = [];
        let count = 0;

        const getVaultSyncAgents = (): Promise<ISyncAgentsDisplay[]> => {
            return this.getVaultSyncAgents(vaultId, count, true).then((resp: AxiosResponse) => {
                count += resp.data.value.length;
                values = [...values, ...resp.data.value];

                if (count < resp.data['@odata.count']) {
                    return getVaultSyncAgents();
                } else {
                    return values;
                }
            });
        };

        return getVaultSyncAgents();
    }

    getVaultPasswordPolicyDefaults(vaultId: string, handleSuccess: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${vaultId})/GetVaultPasswordPolicyDefaults`, {
                onSuccess: handleSuccess
            })
            .catch();
    }

    getAvailableVaultsForExport(
        top: number,
        skip: number,
        handleError: Function,
        handleSuccess: Function,
        search = '',
    ): AxiosPromise {
        return apiService
            .get('/api/export/getAvailableVaults', {
                params: {
                    take: top,
                    skip: skip,
                    filter: search
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    exportVaults(vaultIds: number[]): AxiosPromise {
        return apiService.post(`/api/export/vaults`, vaultIds).catch();
    }

    checkIndividual(): AxiosPromise {
        return apiService.get(`/api/export/checkIndividual`).catch();
    }
}

export const vaultsService = new VaultsService();
